import { useId } from 'react';

import { Box, FormControlLabel, FormHelperText, Switch } from '@mui/material';

import type { TabPeriodSpanDto } from '../api/mergeResolution.types';
import { rangeLabel } from '../state/periodRange';
import type { PeriodRangeMode } from '../state/types';

interface PeriodRangeToggleProps {
  /** Validated span of the active tab; undefined when there is none. */
  span: TabPeriodSpanDto | undefined;
  mode: PeriodRangeMode;
  periodLabel: (dateId: string | null) => string;
  onChange: (mode: PeriodRangeMode) => void;
}

/**
 * Narrows the period columns to the needs-decision range, or widens them to
 * every changed period. Renders nothing when the active tab has no span.
 */
const PeriodRangeToggle = ({
  span,
  mode,
  periodLabel,
  onChange,
}: PeriodRangeToggleProps) => {
  const helperId = useId();

  if (!span) {
    return null;
  }

  const hasNeedsDecision = span.NeedsDecision !== null;
  const checked = hasNeedsDecision && mode === 'needs_decision';
  const outside = span.ChangesOutsideNeedsDecision ?? 0;

  let helperText: string | null = null;
  // "No rows on this tab need a decision" is shown in the Conflicts to resolve
  // header, not here.
  if (hasNeedsDecision && checked && outside > 0) {
    helperText = `${outside} change(s) outside this range are hidden. Turn off to show all changed periods.`;
  }

  const label =
    checked && span.NeedsDecision
      ? `Needs-decision range (${rangeLabel(span.NeedsDecision, periodLabel)})`
      : `All changed periods (${rangeLabel(span.Changed, periodLabel)})`;

  return (
    // Inline with the legend: the helper text sits on the same row as the
    // switch instead of under it, so the toggle adds no height.
    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <Switch
            size="small"
            checked={checked}
            disabled={!hasNeedsDecision}
            onChange={(_e, next) =>
              onChange(next ? 'needs_decision' : 'changed')
            }
            inputProps={{
              'aria-describedby': helperText ? helperId : undefined,
            }}
          />
        }
        label={label}
      />
      {helperText && (
        <FormHelperText id={helperId} sx={{ m: 0 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default PeriodRangeToggle;
